<script setup lang="ts">
  import searchIcon from '@assets/icons/search.svg'
  import { useI18n } from 'vue-i18n'
  import { useDate } from 'vuetify'
  import { useForm } from '@/composables/useForm.ts'
  import type { Chat, Pagination } from '@/types'
  import { AxiosError } from 'axios'
  import { FormError } from '@/utils/FormError.ts'
  import { computed, onMounted, ref } from 'vue'
  import { getChatGroupTitle } from '@/utils/date.ts'

  const { t } = useI18n()
  const dateAdapter = useDate()
  const trackStore = useTrackStore()
  const chats = ref<Chat[]>([])
  const searchInput = useTemplateRef('searchInput')
  const title = defineModel<string | null>()
  const error = ref<FormError | null>(null)

  const { mobile, collapsed } = defineProps<{
    mobile: boolean,
    collapsed: boolean
  }>()

  const focusInput = () => {
    searchInput.value?.focus()
  }

  const fetchChats = async () => {
    try {
      error.value = null
      const response = await useForm<Record<string, unknown>, Pagination<Chat>>('get', '/chats', {
        title: title.value,
        per_page: 100,
        sort: '-updated_at',
      }).submit()

      chats.value = response.data.data
    } catch (e) {
      if (e instanceof AxiosError) {
        error.value = FormError.fromAxiosError(e)
      } else if (e instanceof Error) {
        error.value = new FormError(e.message)
      } else {
        error.value = new FormError('Failed to fetch chats')
      }
    }
  }

  const groupedChats = computed(() => {
    const groups: Record<string, Chat[]> = {}

    chats.value.forEach(chat => {
      const groupTitle = getChatGroupTitle(chat.updated_at ?? chat.created_at, t, dateAdapter)
      if (!groups[groupTitle]) {
        groups[groupTitle] = []
      }
      groups[groupTitle].push(chat)
    })

    return groups
  })

  const fireClickEvent = () => {
    trackStore.track('search_bar_clicked')
  }

  onMounted(() => {
    fetchChats()
  })

  defineExpose({
    focusInput,
  })
</script>

<template>
  <v-text-field
    v-if="mobile || (!mobile && !collapsed)"
    ref="searchInput"
    v-model="title"
    bg-color="search-bar"
    class="w-auto pa-0 mt-0 mb-1 mx-4 flex-grow-0 border-sm border-opacity-100 border-grey-700 rounded-pill"
    clearable
    density="compact"
    flat
    hide-details
    icon-color="grey-500"
    :placeholder="t('search')"
    :prepend-inner-icon="searchIcon"
    rounded="pill"
    variant="solo"
    @click.stop="fireClickEvent"
    @update:model-value="fetchChats"
  />
  <v-alert v-if="error" class="mx-4 mb-2" type="error">
    {{ error.message }}
  </v-alert>
  <v-row v-if="title" class="px-4 ma-0">
    <v-col class="pa-0 mt-8 chat-drawer-content">
      <div v-for="(groupChats, groupTitle) in groupedChats" :key="groupTitle" class="mb-2">
        <h3 class="text-subtitle-2 text-grey-500 font-weight-medium mb-2 px-2">
          {{ groupTitle }}
        </h3>
        <chat-history-side-menu :chats="groupChats" />
      </div>

      <div v-if="chats.length === 0 && !error" class="text-center pa-8 text-grey-400">
        {{ t('no_chats_found') }}
      </div>
    </v-col>
  </v-row>
</template>

<style scoped lang="scss">
.chat-drawer-content {
  height: 30%;
  overflow-y: scroll;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
